import { DialogType } from '@tableau/widgets';
import { InteractiveViz, WorkbookTemplateDescription, WorkbookTemplateThumbnailGallery } from '@tableau/workbook-templates-widgets';
import React from 'react';

import { errorBoundary, sendTelemetryErrorEvent } from '~commonInfra/components/ErrorBoundary';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';

import { Messages } from '../../codegen/Localize';
import { ReactModalService } from '../../commonInfra/dialogs/ReactModalService';
import { HelpService } from '../../commonInfra/services/HelpService';
import { StartersDialogClickSourceOptions, StarterWorkbookCreationSourceOptions } from '../../commonInfra/telemetryEvents/TelemetryEvents';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../sessionManagement/SessionManagementService';
import { starterClickedTelemetryEvent, StarterWorkbookCreationClickedTelemtryEvent } from '../common/telemetryEvents/StartersTelemetry';
import { InteractiveModalDialog } from '../styles/StarterStyles';
import { Starter } from '../types/StartersTypes';
import { buildSessionRelevantInfo } from '../utils/startersHelper';
import { StartersModalCloseButton } from './StartersModalCloseButton';

export const displayDialogWithTemplate = (starter: Starter, startersGroupedByConnectionType: Starter[], source: StartersDialogClickSourceOptions, selectedStarterIndex=0): void => {
  const modal = ReactModalService.open({
    title: Messages.acceleratorsDialogTitle(),
    type: DialogType.Uber,
    shouldReturnFocusOnClose: true,
    // Widget requires a number (only really used a number for the resizable dialog) but we want to use vw
    // we cannot cast string to number so casting it to unknown and then to number.
    dialogWidth: InteractiveModalDialog.InteractiveModalDialogWidth + 'vw' as unknown as number,
    dialogContent:<InteractiveModalContent
      startersGroupedByConnectionType={startersGroupedByConnectionType}
      selectedStarterIndex={selectedStarterIndex}
      handleCloseButton={() => {modal.close();}}
    />,
    showCloseButton: true, // TODO: Add button, this does not work for uber dialogs
    dialogStyle: {
      padding: '0',
    }
  });
  starterClickedTelemetryEvent(starter, selectedStarterIndex, source);
};

export interface InteractiveModalProps{
  startersGroupedByConnectionType: Starter[];
  selectedStarterIndex: number;
  handleCloseButton: () => void;
}

interface InteractiveModalState{
  starterThumbnailIndex: number;
}

export class InteractiveModalContentInternal extends React.PureComponent<InteractiveModalProps, InteractiveModalState> {

  private readonly externalVersion: string;
  private readonly toasterService: ToasterService;
  private readonly sessionManagementService: SessionManagementService;

  constructor(props: InteractiveModalProps) {
    super(props);
    this.state = {starterThumbnailIndex: props.selectedStarterIndex};
    this.toasterService = ToasterService.instance;
    this.externalVersion = HelpService.externalVersion();
    this.sessionManagementService = getSessionManagementServiceInstance();
  }

  handleSelectedThumbnail = (selectedThumbnailIndex: number) => {
    this.setState({starterThumbnailIndex: selectedThumbnailIndex});
    starterClickedTelemetryEvent(this.props.startersGroupedByConnectionType[selectedThumbnailIndex], selectedThumbnailIndex, StartersDialogClickSourceOptions.Dialog);
  };

  private onTemplateFailed = (): void => {
    this.toasterService.pop({
      text: Messages.acceleratorsWorkbookCreationError(),
      type: ToastType.Error,
    });
  };

  private sendWorkbookCreationTelemetry = (starter: Starter, withSampleData: boolean) => {
    StarterWorkbookCreationClickedTelemtryEvent(
      StarterWorkbookCreationSourceOptions.Dialog,
      starter,
      withSampleData,
    );
  };

  render() {
    return (
      <div className={InteractiveModalDialog.DialogContent}>
        <div className={InteractiveModalDialog.VizAndCarouselGroup}>
          <div className={InteractiveModalDialog.InteractiveViz} data-tb-test-id={'interactive-modal-content-viz'}>
            <InteractiveViz template={this.props.startersGroupedByConnectionType[this.state.starterThumbnailIndex]}/>
          </div>
          <div className={InteractiveModalDialog.ThumbnailCarousel}
            data-tb-test-id={'interactive-modal-content-carousel'}>
            <WorkbookTemplateThumbnailGallery
              handleSelectedThumbnail={this.handleSelectedThumbnail}
              selectedStarterIndex={this.state.starterThumbnailIndex}
              startersThumbnails={this.props.startersGroupedByConnectionType.map((starter: Starter) => {
                return {url: starter.thumbnailUrl, name: starter.name};
              })}
            />
          </div>
        </div>
        <div className={InteractiveModalDialog.DescriptionSection}>
          <WorkbookTemplateDescription
            template={this.props.startersGroupedByConnectionType[this.state.starterThumbnailIndex]}
            onTemplateSelected={() => {
            }}
            isVpc={true}
            helpVersion={this.externalVersion}
            onTemplateCreationFailed={this.onTemplateFailed}
            sendTelemetry={this.sendWorkbookCreationTelemetry}
            sessionRelevantInfo={buildSessionRelevantInfo(this.sessionManagementService)}
          />
        </div>
        <StartersModalCloseButton handleClick={this.props.handleCloseButton}
                                  tooltipText={Messages.acceleratorsCloseButtonAltText()}/>
      </div>);
  }
}

export const InteractiveModalContent = errorBoundary<InteractiveModalProps>(sendTelemetryErrorEvent)(InteractiveModalContentInternal);
